/**
 * FONTE ÚNICA da Política de Privacidade.
 *
 * Este arquivo é o original. A partir dele:
 *   - PoliticaPrivacidade.js renderiza a página em /privacidade
 *   - `npm run gerar-politica` escreve POLITICA_PRIVACIDADE.md na raiz do projeto
 *
 * NÃO edite o .md diretamente — ele é gerado e será sobrescrito.
 *
 * Formatação inline suportada no campo `texto`:
 *   **negrito**            → <strong> no React, ** no markdown
 *   [rótulo](destino)      → <a> no React, link no markdown
 *
 * Ao publicar uma revisão relevante, atualize VERSAO e ATUALIZADA abaixo, e
 * espelhe a VERSAO na constante POLITICA_VERSAO do nfse-api/main.py — é o valor
 * gravado no cadastro de cada usuário como prova de qual texto ele leu.
 */

export const VERSAO = "2026-09-02";
export const ATUALIZADA = "2 de setembro de 2026";
export const EMPRESA = "Inatec Contabilidade LTDA";
export const CONTATO = "jeancarlo@inateccontabilidade.com.br";

export const CONTEUDO = [
  { tipo: "paragrafo", texto:
    `Esta política descreve como a **${EMPRESA}** trata dados pessoais no sistema de emissão de ` +
    `Nota Fiscal de Serviço eletrônica (NFS-e), em conformidade com a Lei nº 13.709/2018 — ` +
    `Lei Geral de Proteção de Dados (LGPD).` },

  { tipo: "secao", titulo: "1. Quem trata seus dados" },
  { tipo: "paragrafo", texto:
    `A ${EMPRESA} atua em duas qualidades distintas, conforme o tipo de dado.` },
  { tipo: "paragrafo", texto:
    "**Como controladora**, quanto aos dados de cadastro dos usuários do sistema — quem cria conta " +
    "e acessa a plataforma. Nesses casos, é a Inatec quem decide as finalidades e os meios do tratamento." },
  { tipo: "paragrafo", texto:
    "**Como operadora**, quanto aos dados de tomadores de serviço inseridos pelos usuários para " +
    "emissão de notas fiscais. Nesses casos, o usuário do sistema é o controlador — é ele quem decide " +
    "quais notas emitir e para quem —, e a Inatec apenas processa os dados conforme essa determinação " +
    "e as exigências da legislação fiscal." },
  { tipo: "paragrafo", texto:
    `**Contato para assuntos de privacidade:** [${CONTATO}](mailto:${CONTATO})` },

  { tipo: "subsecao", titulo: "1.1 Quem pode entrar na sua conta" },
  { tipo: "paragrafo", texto:
    "Além de você, duas situações permitem que outra pessoa acesse sua conta:" },
  { tipo: "lista", itens: [
    `**Administradores do sistema**, da equipe da ${EMPRESA}, para dar suporte e resolver problemas.`,
    `**Contadores vinculados à sua conta.** A ${EMPRESA} pode autorizar um contador da equipe a ` +
      "acessar a conta de um cliente para operar em seu nome.",
  ]},
  { tipo: "paragrafo", texto:
    "Quem entra dessa forma **vê e faz tudo o que você faria**: consulta notas emitidas e dados de " +
    "tomadores, emite e cancela notas, altera cadastros de empresas e serviços. O contador vinculado " +
    "**não recebe** acesso ao painel administrativo do sistema, nem à conta de qualquer outro cliente " +
    "além daqueles a que foi expressamente vinculado." },
  { tipo: "destaque", texto:
    "Enquanto alguém está acessando sua conta dessa forma, uma faixa permanece visível no alto da " +
    "tela identificando quem entrou. Toda entrada fica registrada, com data, hora e endereço IP." },
  { tipo: "paragrafo", texto:
    "A criação e a remoção desses vínculos são feitas apenas por administradores e também ficam " +
    "registradas. Você pode pedir a qualquer momento a lista de quem tem acesso à sua conta, ou " +
    "solicitar a remoção de um vínculo, pelo contato de privacidade acima." },

  { tipo: "secao", titulo: "2. Dados tratados" },

  { tipo: "subsecao", titulo: "2.1 Dados dos usuários do sistema" },
  { tipo: "tabela", cabecalho: ["DADO", "FINALIDADE"], linhas: [
    ["Nome e e-mail", "Identificação, autenticação e comunicação"],
    ["Senha", "Guardada de forma criptografada e irreversível; nunca armazenamos sua senha legível"],
    ["Plano contratado", "Controle de acesso a funcionalidades"],
    ["Data de cadastro", "Registro contratual"],
  ]},

  { tipo: "subsecao", titulo: "2.2 Dados das empresas emitentes" },
  { tipo: "paragrafo", texto:
    "Razão social, CNPJ, inscrição municipal, endereço, telefone e e-mail, além do certificado " +
    "digital A1 e sua respectiva senha, necessários para assinar digitalmente os documentos fiscais." },

  { tipo: "subsecao", titulo: "2.3 Dados de tomadores de serviço" },
  { tipo: "paragrafo", texto:
    "Para emitir uma NFS-e, a legislação exige a identificação do tomador. São tratados:" },
  { tipo: "lista", itens: [
    "CPF ou CNPJ",
    "Nome ou razão social",
    "Endereço completo (logradouro, número, bairro, município, UF, CEP)",
    "E-mail e telefone, quando informados",
    "Para tomadores no exterior: NIF, país, município e endereço estrangeiros",
  ]},
  { tipo: "paragrafo", texto:
    "Esses dados são fornecidos pelo usuário do sistema, que responde por sua origem e legitimidade." },

  { tipo: "subsecao", titulo: "2.4 Dados de suporte" },
  { tipo: "paragrafo", texto:
    'Ao utilizar a função "Reportar erro", são enviados o nome e o e-mail do usuário, a descrição do ' +
    "problema e, opcionalmente, imagens anexadas. **Atenção:** capturas de tela podem conter dados de " +
    "tomadores; recomenda-se ocultar informações sensíveis antes de anexar." },

  { tipo: "subsecao", titulo: "2.5 Registros de atividade" },
  { tipo: "paragrafo", texto:
    "O sistema mantém um registro das ações realizadas em cada conta. Para cada evento são gravados " +
    "**data e hora, o usuário responsável, a ação realizada e o endereço IP de origem**, além da " +
    "identificação do que foi afetado — por exemplo, o número e o valor da nota emitida. " +
    "São registrados:" },
  { tipo: "lista", itens: [
    "Entradas e saídas do sistema, **incluindo tentativas de acesso com senha incorreta**",
    "Emissão, cancelamento e exclusão de notas fiscais",
    "Download dos XML das notas em lote",
    "Cadastro, alteração e exclusão de empresas, serviços e pessoas",
    "Envio e substituição de certificados digitais",
    "Acessos administrativos a contas de usuários",
    "Criação e remoção de vínculos de contador, e cada entrada de contador em conta vinculada",
  ]},
  { tipo: "paragrafo", texto:
    "**Na alteração de cadastro de empresa, o registro guarda o valor anterior e o novo** de cada " +
    "campo modificado — por exemplo, o e-mail ou o telefone antes e depois da mudança. Isso permite " +
    "saber exatamente o que foi alterado, e não apenas que houve uma alteração." },
  { tipo: "paragrafo", texto:
    "Esse registro existe para dar segurança a você e à sua empresa: permite identificar quem emitiu " +
    "cada nota, detectar acesso indevido e comprovar o que ocorreu em caso de incidente. **O acesso a " +
    "esses registros é restrito aos administradores do sistema.**" },

  { tipo: "secao", titulo: "3. Base legal" },
  { tipo: "tabela", cabecalho: ["TRATAMENTO", "BASE LEGAL (LGPD ART. 7º)"], linhas: [
    ["Cadastro e autenticação de usuários", "Execução de contrato (inciso V)"],
    ["Emissão de NFS-e e guarda dos documentos", "Cumprimento de obrigação legal ou regulatória (inciso II)"],
    ["Transmissão ao Sistema Nacional da NFS-e", "Cumprimento de obrigação legal (inciso II)"],
    ["Comunicações operacionais por e-mail", "Execução de contrato (inciso V)"],
    ["Registros de acesso ao sistema (logs)", "Cumprimento de obrigação legal (inciso II) — Marco Civil da Internet, art. 15"],
    ["Demais registros de atividade e segurança", "Legítimo interesse (inciso IX)"],
  ]},

  { tipo: "secao", titulo: "4. Compartilhamento de dados" },
  { tipo: "paragrafo", texto:
    "Os dados são compartilhados exclusivamente quando necessário à prestação do serviço ou por " +
    "exigência legal:" },
  { tipo: "lista", itens: [
    "**Sistema Nacional da NFS-e**, do Governo Federal — recebe os dados da nota fiscal por imposição legal. É o próprio ato de emitir a nota.",
    "**IBPT** (Instituto Brasileiro de Planejamento e Tributação) — os percentuais de tributos aproximados que a lei obriga a informar na nota vêm de uma tabela que mantemos em nossos próprios servidores. Só quando um código não consta nessa tabela é feita uma consulta ao IBPT, e ela leva apenas o código do tipo de serviço. Nenhum dado pessoal é enviado.",
    "**Provedor de e-mail (Google/Gmail)** — envio de mensagens transacionais, como redefinição de senha.",
    "**KingHost** — hospedagem da infraestrutura, com servidores localizados no Brasil.",
    "**BrasilAPI** — consultada com o CNPJ digitado, para preencher automaticamente razão social e endereço da empresa e poupar digitação.",
    "**IBGE** — consultado para obter a lista de municípios e seus códigos oficiais, exigidos na nota fiscal.",
  ]},
  { tipo: "paragrafo", texto:
    "As consultas à BrasilAPI e ao IBGE partem do seu próprio navegador, e não dos nossos servidores. " +
    "Isso significa que o seu endereço IP fica visível para esses serviços. Nenhum dado de tomador, " +
    "valor de nota ou informação de conta é enviado a eles — apenas o CNPJ ou o município consultado." },
  { tipo: "paragrafo", texto:
    "A fonte tipográfica do sistema é hospedada em nossos próprios servidores. **Nenhuma requisição " +
    "é feita a redes de distribuição de conteúdo de terceiros**, de modo que o endereço IP dos " +
    "visitantes não é exposto a nenhuma empresa além das listadas acima." },
  { tipo: "paragrafo", texto:
    "Os dados **não são vendidos, cedidos ou utilizados para publicidade** em nenhuma hipótese." },

  { tipo: "secao", titulo: "5. O que fica salvo no seu navegador" },
  { tipo: "destaque", texto:
    "Este sistema não utiliza cookies e não possui ferramentas de rastreamento, analytics ou publicidade." },
  { tipo: "paragrafo", texto:
    "Não acompanhamos sua navegação, dentro ou fora do sistema. Para funcionar, o sistema guarda " +
    "algumas informações no seu próprio computador:" },
  { tipo: "lista", itens: [
    "**Sua sessão de login**, para que você não precise digitar a senha a cada página",
    "**Sua preferência de tema**, claro ou escuro",
    "**Um rascunho do que você está digitando** ao emitir uma nota, para que nada se perca se a página fechar por acidente",
    "**Uma cópia dos arquivos do próprio sistema** — imagens, fontes e código da tela —, para abrir mais rápido e mostrar um aviso claro quando você estiver sem internet. Nenhum dado seu, de nota ou de tomador fica nessa cópia",
  ]},
  { tipo: "paragrafo", texto:
    "O sistema pode ser instalado como aplicativo no celular ou no computador. A instalação apenas " +
    "cria um atalho e faz o sistema abrir em tela cheia: **nada muda no que é guardado ou enviado**, " +
    "e nenhum acesso adicional ao seu aparelho é solicitado." },
  { tipo: "paragrafo", texto:
    "Essas informações ficam apenas no seu equipamento e não são enviadas a terceiros. Por serem " +
    "necessárias ao funcionamento do serviço, não dependem de consentimento prévio." },
  { tipo: "paragrafo", texto:
    "**Ao sair do sistema, tudo o que se refere a você é apagado** — sessão, preferências e os " +
    "rascunhos, que podem conter dados de tomadores. Permanece apenas a cópia dos arquivos do " +
    "sistema, que não contém informação nenhuma sua. Você pode remover tudo quando quiser, pela " +
    "função de limpar dados de navegação do seu navegador." },

  { tipo: "secao", titulo: "6. Prazo de retenção" },
  { tipo: "paragrafo", texto:
    "**Documentos fiscais** (notas emitidas, XML e dados de tomadores): mantidos por 5 anos, conforme " +
    "os prazos decadenciais e prescricionais da legislação tributária (CTN, arts. 173 e 174). Nesse " +
    "período, pedidos de exclusão não podem ser atendidos por prevalecer obrigação legal de guarda " +
    "(LGPD, art. 16, I)." },
  { tipo: "paragrafo", texto:
    "**Dados cadastrais de usuários:** mantidos enquanto a conta estiver ativa e por até 5 anos após " +
    "o encerramento." },
  { tipo: "paragrafo", texto:
    "**Certificados digitais:** mantidos enquanto vigentes e vinculados a uma empresa ativa; " +
    "excluídos com a exclusão da empresa." },
  { tipo: "paragrafo", texto:
    "**Registros de atividade (logs):** mantidos por 12 meses e descartados automaticamente após " +
    "esse prazo. O Marco Civil da Internet (art. 15) exige a guarda dos registros de acesso por, no " +
    "mínimo, 6 meses — adotamos prazo maior para permitir a investigação de incidentes que só sejam " +
    "identificados tempos depois." },
  { tipo: "paragrafo", texto:
    "**Cópias de segurança:** retidas por 30 dias, em rotação automática." },

  { tipo: "secao", titulo: "7. Segurança" },
  { tipo: "paragrafo", texto: "Adotamos as seguintes medidas para proteger seus dados:" },
  { tipo: "lista", itens: [
    "**Sua senha não fica guardada de forma legível.** Ela é transformada por um processo irreversível — nem a equipe da Inatec consegue vê-la. Por isso, senha esquecida é redefinida, nunca recuperada.",
    "**A senha do seu certificado digital é armazenada criptografada.**",
    "**Toda a comunicação** entre seu navegador e nossos servidores trafega criptografada.",
    "**Cada conta acessa apenas os próprios dados**, sem qualquer cruzamento entre clientes.",
    "**O número de tentativas de login é limitado**, para impedir que alguém tente adivinhar senhas.",
    "**As sessões são encerradas** ao sair do sistema ou ao alterar a senha.",
    "**Todas as ações relevantes ficam registradas** — inclusive acessos administrativos a contas —, com data, hora, responsável e origem.",
    "**Cópias de segurança diárias** do banco de dados.",
  ]},
  { tipo: "paragrafo", texto:
    "Nenhum sistema é imune a incidentes. Em caso de incidente de segurança com risco relevante aos " +
    "titulares, a Inatec comunicará os afetados e a ANPD, nos termos do art. 48 da LGPD." },

  { tipo: "secao", titulo: "8. Direitos do titular" },
  { tipo: "paragrafo", texto: "Nos termos do art. 18 da LGPD, você pode solicitar:" },
  { tipo: "lista", itens: [
    "**Confirmação** da existência de tratamento",
    "**Acesso** aos seus dados",
    "**Correção** de dados incompletos, inexatos ou desatualizados",
    "**Anonimização, bloqueio ou eliminação** de dados desnecessários ou tratados em desconformidade",
    "**Portabilidade** a outro fornecedor",
    "**Informação** sobre com quem os dados foram compartilhados",
    "**Revogação do consentimento**, quando essa for a base legal aplicada",
  ]},
  { tipo: "paragrafo", texto:
    `Solicitações devem ser enviadas a [${CONTATO}](mailto:${CONTATO}), com resposta em até 15 dias.` },
  { tipo: "aviso", texto:
    "**Limitação importante:** dados que integram documentos fiscais já emitidos não podem ser " +
    "excluídos antes do prazo legal de guarda, por força do art. 16, I, da LGPD. Nesses casos, " +
    "informaremos o fundamento da recusa." },
  { tipo: "paragrafo", texto:
    "Se você é tomador de serviço e teve seus dados incluídos em uma nota fiscal, o controlador " +
    "desses dados é o prestador que emitiu a nota. Podemos intermediar o contato." },

  { tipo: "secao", titulo: "9. Melhorias em andamento" },
  { tipo: "paragrafo", texto:
    "Em nome da transparência, registramos itens ainda em adequação:" },
  { tipo: "lista", itens: [
    "Rotina automática de descarte de dados após o prazo legal de retenção de 5 anos",
    "Migração dos relatórios de erro para canal corporativo dedicado, substituindo o encaminhamento atual por e-mail",
  ]},

  { tipo: "secao", titulo: "10. Alterações" },
  { tipo: "paragrafo", texto:
    "Esta política pode ser atualizada. Alterações relevantes serão comunicadas por e-mail ou aviso " +
    "no sistema. A data no topo indica a última revisão." },
];

/** Pontos que dependem de revisão jurídica — vão só para o .md, não para a página. */
export const NOTA_INTERNA = [
  "razão social completa e CNPJ da empresa",
  "confirmação do enquadramento controlador/operador da seção 1",
  "indicação formal do Encarregado (DPO) exigida pelo art. 41 da LGPD",
  "validação dos prazos de retenção à luz da legislação fiscal municipal aplicável",
];
